import { normalizeOptions as normalizeThreeInsulationOptions } from '../three/three_insulation_options.js?v=20260706d';
import { getCurrentUser, isAdminUser as isCurrentUserAdmin } from '../user/current_user.js?v=20260722a';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { S } from './app_state.js?v=20260817c';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import {
  openAssetDownloadModal,
  openAssetObjectImportModal,
  openBugReportModal,
  openCatalogReleaseAdminModal,
  openColorCatalogAdminModal,
  openSystemOptionsAdminModal,
  openContextMenuAdminModal,
  openCustomerMasterAdminModal,
  openHeaderAdminModal,
  openInfosAdminModal,
  openLanguageAdminModal,
  openMessageSettingsAdminModal,
  openProductMasterAdminModal,
  openPrintSettingsAdminModal,
  openStatisticsAdminModal,
  openToolbarAdminModal,
  openUserAdminModal
} from './lazy_dialog_runtime.js?v=20260904f';
import { AppRuntime } from './app_runtime.js?v=20260706d';
import { flagIconSvg } from '../utils/flag_icons.js?v=20260730a';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import {
  PLAN_LABEL_SIZE_PRESETS,
  currentPlanLabelSize,
  setCurrentPlanLabelSize
} from '../render/plan_label_options.js?v=20260828b';
import {
  availableLanguagesList,
  getActiveLanguage,
  hasI18nHealthIssue,
  setActiveLanguage,
  t
} from '../i18n/i18n_service.js?v=20260722a';

const STORAGE_KEY = 'ks-studio-app-options-v1';
// Vom Login-Gate (src/main.js) NUR bei einem tatsaechlichen Login-Absenden
// gesetzt (nicht bei einer stillen Session-Wiederherstellung) — hier sofort
// nach dem Lesen wieder geloescht (Einmal-Konsum), damit die Gate-Sprache
// GENAU EINMAL, im Moment des Logins, die Session-Sprache setzt und nicht bei
// jedem spaeteren Neuladen eine zwischenzeitlich in den Optionen bewusst
// gewaehlte Sprache ueberschreibt.
const GATE_LANGUAGE_FRESH_LOGIN_KEY = 'ks-studio-gate-language-fresh-login-v1';
const GRID_SIZES = [10, 50, 70, 100, 600, 750];
const runtimeCallbacks = {
  refresh: null,
  refreshChromeOnly: null
};

export function configureRuntime(callbacks = {}) {
  if (typeof callbacks.refresh === 'function') runtimeCallbacks.refresh = callbacks.refresh;
  if (typeof callbacks.refreshChromeOnly === 'function') runtimeCallbacks.refreshChromeOnly = callbacks.refreshChromeOnly;
}

function requestRefresh(fit = false) {
  if (typeof runtimeCallbacks.refresh === 'function') runtimeCallbacks.refresh(Boolean(fit));
}

function requestChromeRefresh(reason = '') {
  if (typeof runtimeCallbacks.refreshChromeOnly === 'function') {
    runtimeCallbacks.refreshChromeOnly(reason);
  } else {
    requestRefresh(false);
  }
}

export function readStoredAppOptions() {
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_err) {
    return null;
  }
}

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function currentUserOptionsKey() {
  const user = getCurrentUser();
  const userId = String(user?.benutzer_id ?? user?.id ?? '').trim();
  if (userId) return `id:${userId}`;
  const loginName = String(user?.login_name ?? user?.loginName ?? '').trim().toLowerCase();
  if (loginName) return `login:${loginName}`;
  const email = String(user?.email ?? '').trim().toLowerCase();
  return email ? `email:${email}` : '';
}

function storedUserOptions(options = readStoredAppOptions()) {
  const key = currentUserOptionsKey();
  const userOptions = plainObject(options?.userOptions);
  return key ? plainObject(userOptions[key]) : {};
}

function consumeFreshLoginGateLanguage() {
  try {
    const value = String(window.localStorage?.getItem(GATE_LANGUAGE_FRESH_LOGIN_KEY) || '').trim().toUpperCase();
    window.localStorage?.removeItem(GATE_LANGUAGE_FRESH_LOGIN_KEY);
    return value;
  } catch (_err) {
    return '';
  }
}

export function persistAppOptions() {
  try {
    const previousOptions = plainObject(readStoredAppOptions());
    const userOptionsKey = currentUserOptionsKey();
    const userOptions = { ...plainObject(previousOptions.userOptions) };
    if (userOptionsKey) {
      userOptions[userOptionsKey] = {
        ...plainObject(userOptions[userOptionsKey]),
        autoOpenLastOrder: S.ui?.autoOpenLastOrder === true,
        language: getActiveLanguage()
      };
    }
    const payload = {
      snap: Boolean(S.project.snap),
      showGrid: Boolean(S.project.showGrid),
      showHelperGrid: Boolean(S.project.showHelperGrid),
      gridSize: GRID_SIZES.includes(Number(S.project.gridSize)) ? Number(S.project.gridSize) : 10,
      helperGridSize: GRID_SIZES.includes(Number(S.project.helperGridSize)) ? Number(S.project.helperGridSize) : 600,
      projectGridOptionsTouched: S.ui?.projectGridOptionsTouched === true,
      warnUnsavedChanges: S.ui?.warnUnsavedChanges !== false,
      showCoordinateAxis: S.ui?.showCoordinateAxis !== false,
      view3dInsulation: ensureView3DInsulationOptions(),
      activeCompanyId: SystemRegistry.state.activeCompanyId,
      activeCategoryId: SystemRegistry.state.activeCategoryId,
      activeSystemId: SystemRegistry.state.activeSystemId,
      userOptions
    };
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_err) {
    // Die App bleibt auch ohne Browser-Speicher voll nutzbar.
  }
}

export function applyStoredAppOptions() {
  // Sprache, die der Nutzer gerade eben beim Absenden des Logins auf dem
  // Gate gewaehlt hatte (src/main.js, GATE_LANGUAGE_FRESH_LOGIN_KEY), hat
  // EINMALIG Vorrang vor der zuletzt gespeicherten Sprache dieses
  // Benutzerkontos — eine bewusste, frische Wahl im Moment des Logins
  // schlaegt eine womoeglich veraltete frühere Einstellung.
  // consumeFreshLoginGateLanguage() loescht den Key sofort nach dem Lesen,
  // damit er nicht bei jedem spaeteren Neuladen erneut eine zwischenzeitlich
  // in den Optionen bewusst gewaehlte Sprache ueberschreibt. Wird am Ende
  // dieser Funktion (nachdem
  // alles andere aus dem Storage wiederhergestellt ist) in den Benutzer-
  // Bucket zurueckgeschrieben, siehe persistAppOptions()-Aufruf unten.
  const gateLanguage = consumeFreshLoginGateLanguage();
  const options = readStoredAppOptions();
  if (!options || typeof options !== 'object') {
    if (gateLanguage) {
      if (!S.ui || typeof S.ui !== 'object') S.ui = {};
      S.ui.language = gateLanguage;
      persistAppOptions();
    }
    return;
  }

  const hasStoredProjectGridOptions = ['snap', 'showGrid', 'showHelperGrid', 'gridSize', 'helperGridSize']
    .some(key => Object.prototype.hasOwnProperty.call(options, key));
  if (options.projectGridOptionsTouched === true || hasStoredProjectGridOptions) markProjectGridOptionsTouched();
  if (typeof options.snap === 'boolean') S.project.snap = options.snap;
  if (typeof options.showGrid === 'boolean') S.project.showGrid = options.showGrid;
  if (typeof options.showHelperGrid === 'boolean') S.project.showHelperGrid = options.showHelperGrid;
  if (GRID_SIZES.includes(Number(options.gridSize))) S.project.gridSize = Number(options.gridSize);
  if (GRID_SIZES.includes(Number(options.helperGridSize))) S.project.helperGridSize = Number(options.helperGridSize);
  if (options.view3dInsulation && S.view3d) {
    S.view3d.insulation = normalizeThreeInsulationOptions(options.view3dInsulation);
  }
  S.project.currentView = 'plan';
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.warnUnsavedChanges = typeof options.warnUnsavedChanges === 'boolean'
    ? options.warnUnsavedChanges
    : true;
  S.ui.showCoordinateAxis = typeof options.showCoordinateAxis === 'boolean'
    ? options.showCoordinateAxis
    : true;
  const userOptions = storedUserOptions(options);
  S.ui.autoOpenLastOrder = typeof userOptions.autoOpenLastOrder === 'boolean'
    ? userOptions.autoOpenLastOrder
    : options.autoOpenLastOrder === true;
  if (gateLanguage) {
    S.ui.language = gateLanguage;
  } else if (userOptions.language) {
    S.ui.language = String(userOptions.language).toUpperCase();
  }
  if (options.activeCompanyId && SystemRegistry.getCompany(options.activeCompanyId)) {
    SystemRegistry.state.activeCompanyId = options.activeCompanyId;
  }
  if (options.activeCategoryId && SystemRegistry.getCategory(options.activeCategoryId)) {
    const category = SystemRegistry.getCategory(options.activeCategoryId);
    SystemRegistry.state.activeCategoryId = category.id;
    if (category.companyId) SystemRegistry.state.activeCompanyId = category.companyId;
  }
  if (options.activeSystemId && SystemRegistry.getSystem(options.activeSystemId)) {
    SystemRegistry.state.activeSystemId = options.activeSystemId;
    const system = SystemRegistry.getSystem(options.activeSystemId);
    if (system?.categoryId) SystemRegistry.state.activeCategoryId = system.categoryId;
    if (system?.companyId) SystemRegistry.state.activeCompanyId = system.companyId;
  }
  SystemRegistry.normalizeNavigationState();
  if (gateLanguage) persistAppOptions();
}

export function shouldAutoOpenLastOrder() {
  return S.ui?.autoOpenLastOrder === true;
}

export function markProjectGridOptionsTouched() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.projectGridOptionsTouched = true;
}

export function ensureView3DInsulationOptions() {
  if (!S.view3d || typeof S.view3d !== 'object') S.view3d = {};
  S.view3d.insulation = normalizeThreeInsulationOptions(S.view3d.insulation);
  return S.view3d.insulation;
}

export function syncAppOptionsButton() {
  const button = document.getElementById('btnAppOptions');
  if (!button) return;
  button.classList.toggle('is-active', Boolean(S.ui?.showAppOptions));
  button.setAttribute('aria-pressed', S.ui?.showAppOptions ? 'true' : 'false');
}

export function syncAppAdminButton() {
  const button = document.getElementById('btnAppAdmin');
  if (!button) return;
  const admin = isAdminUser();
  button.hidden = !admin;
  button.classList.toggle('is-active', admin && Boolean(S.ui?.showAppAdminPanel));
  button.classList.toggle('has-i18n-gap', admin && hasI18nHealthIssue());
  button.setAttribute('aria-pressed', admin && S.ui?.showAppAdminPanel ? 'true' : 'false');
}

export function isAdminUser() {
  return isCurrentUserAdmin();
}

export function renderAppOptionsPanel() {
  const host = document.getElementById('selectionPanel');
  if (!host) return;
  SystemRegistry.normalizeNavigationState();
  const activeCompany = SystemRegistry.activeCompany() || null;
  const activeCategory = SystemRegistry.activeCategory() || null;
  const activeSystem = SystemRegistry.activeSystem();
  const companies = SystemRegistry.allCompanies();
  const categories = SystemRegistry.categoriesForCompany(SystemRegistry.state.activeCompanyId);
  const systems = SystemRegistry.systemsForCategory(SystemRegistry.state.activeCategoryId);
  const planLabelSize = currentPlanLabelSize();
  const planLabelPresetSummary = PLAN_LABEL_SIZE_PRESETS
    .map(preset => `${t(preset.labelKey, preset.fallbackLabel)} ${preset.fontSizeMm} mm`)
    .join(' · ');
  host.className = 'app-options-panel';
  host.innerHTML = `
    <div class="card">
      <div class="app-options-header">
        <div class="card-title">${escapeHtml(t('sidebar_rechts.app_optionen.titel.karte', 'App-Optionen'))}</div>
        <button id="btnCloseAppOptions" class="btn icon-btn" type="button" aria-label="App-Optionen schliessen" title="Schliessen">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      <div class="small-text note">${escapeHtml(t('sidebar_rechts.app_optionen.wert.beschreibung', 'Globale Einstellungen fuer Arbeitsflaeche und aktuelle Ansicht.'))}</div>
    </div>
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.startansicht.titel.karte', 'Startansicht'))}</div>
      <div class="app-options-grid app-options-filter-grid">
        <label>${escapeHtml(t('sidebar_rechts.startansicht.label.company', 'Company'))}
          <select id="optActiveCompany">
            ${companies.map(company => `<option value="${company.id}" ${activeCompany?.id === company.id ? 'selected' : ''}>${escapeHtml(company.label)}</option>`).join('')}
          </select>
        </label>
        <label>${escapeHtml(t('sidebar_rechts.startansicht.label.systemgruppe', 'Systemgruppe'))}
          <select id="optActiveSystemGroup">
            ${categories.map(category => `<option value="${category.id}" ${activeCategory?.id === category.id ? 'selected' : ''}>${escapeHtml(category.label)}</option>`).join('')}
          </select>
        </label>
        <label>${escapeHtml(t('sidebar_rechts.startansicht.label.system', 'System'))}
          <select id="optActiveSystem">
            ${systems.map(system => `<option value="${system.id}" ${activeSystem?.id === system.id ? 'selected' : ''}>${escapeHtml(system.label)}</option>`).join('')}
          </select>
        </label>
        <label>${escapeHtml(t('sidebar_rechts.startansicht.label.auftrag_oeffnen', 'Letzten Auftrag beim Start öffnen'))}<input id="optAutoOpenLastOrder" type="checkbox" ${S.ui?.autoOpenLastOrder === true ? 'checked' : ''}></label>
      </div>
    </div>
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.sprachen.titel.karte', 'Sprachen'))}</div>
      <div class="app-options-language-switch" role="group" aria-label="Sprache wählen">
        ${availableLanguagesList().map(language => `
          <button
            class="btn icon-btn app-options-language-btn${getActiveLanguage() === String(language.sprache_code).toUpperCase() ? ' is-active' : ''}"
            type="button"
            data-language-code="${escapeHtml(language.sprache_code)}"
            title="${escapeHtml(language.sprache_name)}"
            aria-pressed="${getActiveLanguage() === String(language.sprache_code).toUpperCase() ? 'true' : 'false'}"
          >${flagIconSvg(language.sprache_code) || escapeHtml(language.flagge_emoji || language.sprache_code)}</button>
        `).join('')}
      </div>
    </div>
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.arbeitsflaeche.titel.karte', 'Arbeitsflaeche'))}</div>
      <div class="app-options-grid">
        <label>${escapeHtml(t('sidebar_rechts.arbeitsflaeche.label.snap', 'Snap aktiv'))}<input id="optSnap" type="checkbox" ${S.project.snap ? 'checked' : ''}></label>
        <label>${escapeHtml(t('sidebar_rechts.arbeitsflaeche.label.raster', 'Raster anzeigen'))}<input id="optShowGrid" type="checkbox" ${S.project.showGrid ? 'checked' : ''}></label>
        <label>${escapeHtml(t('sidebar_rechts.arbeitsflaeche.label.hilfsraster', 'Hilfsraster anzeigen'))}<input id="optShowHelperGrid" type="checkbox" ${S.project.showHelperGrid ? 'checked' : ''}></label>
        <label>${escapeHtml(t('sidebar_rechts.arbeitsflaeche.label.raster_groesse', 'Raster'))}
          <select id="optGridSize">
            ${GRID_SIZES.map(value => `<option value="${value}" ${Number(S.project.gridSize) === value ? 'selected' : ''}>${value}</option>`).join('')}
          </select>
        </label>
        <label>${escapeHtml(t('sidebar_rechts.arbeitsflaeche.label.hilfsraster_groesse', 'Hilfsraster'))}
          <select id="optHelperGridSize">
            ${GRID_SIZES.map(value => `<option value="${value}" ${Number(S.project.helperGridSize) === value ? 'selected' : ''}>${value}</option>`).join('')}
          </select>
        </label>
      </div>
      <div class="app-options-plan-label-setting">
        <div class="app-options-section-title">${escapeHtml(t('sidebar_rechts.arbeitsflaeche.label.planbeschriftung', 'Planbeschriftung'))}</div>
        <div class="app-options-plan-label-size" role="group" aria-label="${escapeHtml(t('sidebar_rechts.arbeitsflaeche.label.planbeschriftung', 'Planbeschriftung'))}">
          ${PLAN_LABEL_SIZE_PRESETS.map(preset => `
            <button
              class="btn app-options-plan-label-size-btn${planLabelSize === preset.id ? ' is-active' : ''}"
              type="button"
              data-plan-label-size="${preset.id}"
              aria-pressed="${planLabelSize === preset.id ? 'true' : 'false'}"
              title="${preset.fontSizeMm} mm"
            >${escapeHtml(t(preset.labelKey, preset.fallbackLabel))}</button>
          `).join('')}
        </div>
        <div class="small-text note">${escapeHtml(t(
          'sidebar_rechts.arbeitsflaeche.wert.planbeschriftung_hinweis',
          'Feste Modellschriftgrösse:'
        ))} ${escapeHtml(planLabelPresetSummary)}</div>
      </div>
    </div>
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.speichern.titel.karte', 'Speichern'))}</div>
      <div class="app-options-grid">
        <label>${escapeHtml(t('sidebar_rechts.speichern.label.warnen', 'Warnen bei ungespeicherten Zeichnungen'))}<input id="optWarnUnsavedChanges" type="checkbox" ${S.ui?.warnUnsavedChanges !== false ? 'checked' : ''}></label>
        <label>${escapeHtml(t('sidebar_rechts.speichern.label.koordinatensystem', 'Koordinatensystem anzeigen'))}<input id="optShowCoordinateAxis" type="checkbox" ${S.ui?.showCoordinateAxis !== false ? 'checked' : ''}></label>
      </div>
    </div>
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.fehler.titel.karte', 'Fehler'))}</div>
      <button id="btnOpenBugReport" class="btn" type="button">${escapeHtml(t('sidebar_rechts.fehler.button.melden', 'Einen Fehler in der App melden'))}</button>
    </div>
  `;

  document.getElementById('btnCloseAppOptions')?.addEventListener('click', () => {
    S.ui.showAppOptions = false;
    requestRefresh(false);
  });
  document.getElementById('btnOpenBugReport')?.addEventListener('click', () => {
    openBugReportModal().catch(error => {
      console.error('Fehlermeldungsdialog konnte nicht geladen werden', error);
    });
  });
  host.querySelectorAll('[data-language-code]').forEach(button => {
    button.addEventListener('click', async () => {
      const code = button.dataset.languageCode;
      if (code === getActiveLanguage()) return;
      button.disabled = true;
      try {
        await setActiveLanguage(code);
        persistAppOptions();
        await AppRuntime.reloadLocalizedChrome();
        renderAppOptionsPanel();
        requestRefresh(false);
      } finally {
        button.disabled = false;
      }
    });
  });
  document.getElementById('optSnap')?.addEventListener('change', event => {
    recordUndo('Snap ändern');
    markProjectGridOptionsTouched();
    S.project.snap = event.target.checked;
    persistAppOptions();
    requestRefresh(false);
  });
  document.getElementById('optShowGrid')?.addEventListener('change', event => {
    recordUndo('Raster anzeigen');
    markProjectGridOptionsTouched();
    S.project.showGrid = event.target.checked;
    persistAppOptions();
    requestRefresh(false);
  });
  document.getElementById('optShowHelperGrid')?.addEventListener('change', event => {
    recordUndo('Hilfsraster anzeigen');
    markProjectGridOptionsTouched();
    S.project.showHelperGrid = event.target.checked;
    persistAppOptions();
    requestRefresh(false);
  });
  document.getElementById('optGridSize')?.addEventListener('change', event => {
    recordUndo('Raster ändern');
    markProjectGridOptionsTouched();
    S.project.gridSize = Number(event.target.value);
    persistAppOptions();
    requestRefresh(false);
  });
  document.getElementById('optHelperGridSize')?.addEventListener('change', event => {
    recordUndo('Hilfsraster ändern');
    markProjectGridOptionsTouched();
    S.project.helperGridSize = Number(event.target.value);
    persistAppOptions();
    requestRefresh(false);
  });
  host.querySelectorAll('[data-plan-label-size]').forEach(button => {
    button.addEventListener('click', () => {
      if (button.dataset.planLabelSize === planLabelSize) return;
      setCurrentPlanLabelSize(button.dataset.planLabelSize);
      requestRefresh(false);
    });
  });
  document.getElementById('optActiveCompany')?.addEventListener('change', event => {
    // Einstellungen und Header teilen dieselbe Registry-Aktivierung. Der
    // Company-Wechsel aktualisiert dadurch automatisch Systemgruppe und System.
    SystemRegistry.clearSelectionForNavigation(SystemRegistry.state.activeCompanyId, event.target.value);
    SystemRegistry.activateCompany(event.target.value);
    persistAppOptions();
    requestChromeRefresh('options-company-switch');
  });
  document.getElementById('optActiveSystemGroup')?.addEventListener('change', event => {
    // Systemgruppen sind von der aktiven Company abhaengig; beim Wechsel wird
    // das erste passende System derselben Gruppe gewaehlt.
    SystemRegistry.clearSelectionForNavigation(SystemRegistry.state.activeCategoryId, event.target.value);
    SystemRegistry.activateCategory(event.target.value);
    persistAppOptions();
    requestChromeRefresh('options-category-switch');
  });
  document.getElementById('optActiveSystem')?.addEventListener('change', event => {
    // Systemwechsel setzt Company und Systemgruppe ueber das Systemobjekt mit,
    // sodass Header und Einstellungsfilter synchron bleiben.
    SystemRegistry.clearSelectionForNavigation(SystemRegistry.state.activeSystemId, event.target.value);
    SystemRegistry.activateSystem(event.target.value);
    persistAppOptions();
    requestChromeRefresh('options-system-switch');
  });
  document.getElementById('optAutoOpenLastOrder')?.addEventListener('change', event => {
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    S.ui.autoOpenLastOrder = event.target.checked;
    persistAppOptions();
    requestRefresh(false);
  });
  document.getElementById('optWarnUnsavedChanges')?.addEventListener('change', event => {
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    S.ui.warnUnsavedChanges = event.target.checked;
    persistAppOptions();
    requestRefresh(false);
  });
  document.getElementById('optShowCoordinateAxis')?.addEventListener('change', event => {
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    S.ui.showCoordinateAxis = event.target.checked;
    persistAppOptions();
    requestRefresh(false);
  });

}

// K6: In der Zielumgebung (Stable) ist der Katalog schreibgeschuetzt; die Rolle
// kommt einmal pro Sitzung vom Backend.
let catalogRolePromise = null;
function showCatalogReadOnlyNotice(host) {
  catalogRolePromise ||= fetch('/api/admin/catalog-release/status', { credentials: 'same-origin' })
    .then(response => (response.ok ? response.json() : null))
    .then(payload => payload?.role || null)
    .catch(() => null);
  catalogRolePromise.then(role => {
    const notice = host.querySelector('#catalogReadOnlyNotice');
    if (notice) notice.hidden = role !== 'replica';
  });
}

export function renderAppAdminPanel() {
  const host = document.getElementById('selectionPanel');
  if (!host) return false;
  if (!isAdminUser()) {
    if (S.ui) S.ui.showAppAdminPanel = false;
    syncAppAdminButton();
    return false;
  }
  host.className = 'app-admin-panel app-options-panel';
  host.innerHTML = `
    <div class="card">
      <div class="app-options-header">
        <div class="card-title">${escapeHtml(t('sidebar_rechts.administration.titel.karte', 'Administration'))}</div>
        <button id="btnCloseAppAdmin" class="btn icon-btn" type="button" aria-label="Administration schliessen" title="Schliessen">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      <div class="small-text note">${escapeHtml(t('sidebar_rechts.administration.wert.beschreibung', 'Administrative Werkzeuge fuer Datenpflege und Import.'))}</div>
      <div id="catalogReadOnlyNotice" class="catalog-read-only-notice" hidden>${escapeHtml(t('sidebar_rechts.administration.wert.katalog_schreibgeschuetzt', 'Diese Umgebung übernimmt den Katalog aus Dev. Header, Werkzeugleisten, Systemoptionen, Farben, Produktstamm, Assets, Sprachen, Drucken, Infos, Kontextmenü sowie Rollen und Rechte sind hier schreibgeschützt – Änderungen in Dev vornehmen, sie kommen mit dem nächsten Release. Changelog, Benutzer, Kunden, Projekte und Statistik bleiben hier bearbeitbar.'))}</div>
    </div>
    <div class="card">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.einstellungen.titel.karte', 'Einstellungen'))}</div>
      <div class="article-import-actions">
        <button id="btnOpenHeaderAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.header', 'Header'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenToolbarAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.werkzeugleisten', 'Werkzeugleisten'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenSystemOptionsAdmin" class="btn" type="button">Systemoptionen</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenColorCatalogAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.farben_dekore', 'Farben und Dekore'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenAssetDownloads" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.downloads', 'Downloads'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenAdminProjects" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.projekte', 'Projekte'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenStatisticsAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.statistik', 'Statistik'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenCustomerMasterAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.kundenstamm', 'Kundenstamm'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenAdminUserRights" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.benutzer_rechte', 'Benutzer & Rechte'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenAdminBugReports" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.fehlermeldungen', 'Fehlermeldungen'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenMessageSettings" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.nachrichten', 'Nachrichten'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenLanguageAdmin" class="btn${hasI18nHealthIssue() ? ' has-i18n-gap' : ''}" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.sprachen', 'Sprachen'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenContextMenuAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.kontextmenu', 'Kontextmenü'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenProductMasterAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.produktstamm', 'Produktstamm'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenAssetObjectImport" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.asset_objects', 'Assets'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenPrintSettingsAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.drucken', 'Drucken'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenInfosAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.infos', 'Infos'))}</button>
      </div>
      <div class="article-import-actions">
        <button id="btnOpenCatalogReleaseAdmin" class="btn" type="button">${escapeHtml(t('sidebar_rechts.einstellungen.button.release_changelog', 'Release & Changelog'))}</button>
      </div>
    </div>
  `;
  showCatalogReadOnlyNotice(host);
  host.querySelector('#btnCloseAppAdmin')?.addEventListener('click', () => {
    S.ui.showAppAdminPanel = false;
    requestRefresh(false);
  });
  host.querySelector('#btnOpenHeaderAdmin')?.addEventListener('click', () => {
    openHeaderAdminModal().catch(error => {
      console.error('Header-Verwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenToolbarAdmin')?.addEventListener('click', () => {
    openToolbarAdminModal().catch(error => {
      console.error('Werkzeugleisten-Verwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenSystemOptionsAdmin')?.addEventListener('click', () => {
    openSystemOptionsAdminModal().catch(error => console.error('Systemoptionen konnten nicht geladen werden', error));
  });
  host.querySelector('#btnOpenColorCatalogAdmin')?.addEventListener('click', () => {
    openColorCatalogAdminModal().catch(error => {
      console.error('Farben-und-Dekore-Verwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenAssetDownloads')?.addEventListener('click', () => {
    openAssetDownloadModal().catch(error => {
      console.error('Download-Verwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenAdminProjects')?.addEventListener('click', () => {
    openUserAdminModal({ section: 'projects' }).catch(error => {
      console.error('Projektverwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenStatisticsAdmin')?.addEventListener('click', () => {
    openStatisticsAdminModal().catch(error => {
      console.error('Statistik konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenCustomerMasterAdmin')?.addEventListener('click', () => {
    openCustomerMasterAdminModal().catch(error => {
      console.error('Kundenstamm konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenAdminUserRights')?.addEventListener('click', () => {
    openUserAdminModal({ section: 'user-rights' }).catch(error => {
      console.error('Benutzer- und Rechteverwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenAdminBugReports')?.addEventListener('click', () => {
    openUserAdminModal({ section: 'bug-reports' }).catch(error => {
      console.error('Fehlermeldungen konnten nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenMessageSettings')?.addEventListener('click', () => {
    openMessageSettingsAdminModal().catch(error => {
      console.error('Nachrichteneinstellungen konnten nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenLanguageAdmin')?.addEventListener('click', () => {
    openLanguageAdminModal().catch(error => {
      console.error('Sprachverwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenContextMenuAdmin')?.addEventListener('click', () => {
    openContextMenuAdminModal().catch(error => {
      console.error('Kontextmenü-Verwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenProductMasterAdmin')?.addEventListener('click', () => {
    openProductMasterAdminModal().catch(error => {
      console.error('Produktstamm konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenPrintSettingsAdmin')?.addEventListener('click', () => {
    openPrintSettingsAdminModal().catch(error => {
      console.error('Druck-Einstellungen konnten nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenAssetObjectImport')?.addEventListener('click', () => {
    openAssetObjectImportModal().catch(error => {
      console.error('Assets konnten nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenInfosAdmin')?.addEventListener('click', () => {
    openInfosAdminModal().catch(error => {
      console.error('Infos-Verwaltung konnte nicht geladen werden', error);
    });
  });
  host.querySelector('#btnOpenCatalogReleaseAdmin')?.addEventListener('click', () => {
    openCatalogReleaseAdminModal().catch(error => {
      console.error('Release & Changelog konnte nicht geladen werden', error);
    });
  });
  return true;
}
